import { nextDefaultBranchName } from "./branchDefaultName";

export interface SwitchBranchRow {
  id?: string;
  name?: string;
  conditions?: unknown[];
}

const BRANCH_KEYS = ["id", "name", "conditions"] as const;
const MIN_BRANCHES = 1;

export function blankConditionRow() {
  return {
    logic: "OR",
    condition_type: "REGULAR",
    regular_condition: {
      field_type: "TEXT",
      field: "",
      condition: "CONTAINS",
      value: "",
    },
    ai_condition: { prompt: "" },
  };
}

export function serializeSwitchBranches(rows: SwitchBranchRow[]): string {
  return JSON.stringify(
    rows.map((row) => ({
      id: row.id ?? "",
      name: row.name ?? "",
      conditions: Array.isArray(row.conditions) ? row.conditions : [],
    })),
    null,
    2
  );
}

function validateConditions(
  conditions: unknown,
  label: string,
  errors: string[]
): conditions is unknown[] {
  if (!Array.isArray(conditions)) {
    errors.push(`${label}: "conditions" must be an array`);
    return false;
  }
  if (conditions.length === 0) {
    errors.push(`${label}: "conditions" must contain at least one row`);
    return false;
  }
  let valid = true;
  conditions.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") {
      errors.push(`${label}: Condition ${index + 1} must be an object`);
      valid = false;
      return;
    }
    const row = entry as Record<string, unknown>;
    if (typeof row.condition_type !== "string") {
      errors.push(
        `${label}: Condition ${index + 1} is missing "condition_type"`
      );
      valid = false;
    }
  });
  return valid;
}

export function parseSwitchBranchesJson(
  jsonValue: string,
  newId: () => string
): { branches: SwitchBranchRow[]; errors: string[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonValue);
  } catch (error) {
    return {
      branches: [],
      errors: [`Invalid JSON: ${(error as Error).message}`],
    };
  }

  if (!Array.isArray(parsed)) {
    return {
      branches: [],
      errors: ["Value must be an array of branch objects"],
    };
  }

  if (parsed.length < MIN_BRANCHES) {
    return {
      branches: [],
      errors: [`At least ${MIN_BRANCHES} branch is required`],
    };
  }

  const errors: string[] = [];
  const seenIds = new Set<string>();
  const usedNames: string[] = [];
  const branches: SwitchBranchRow[] = [];

  parsed.forEach((entry, index) => {
    const label = `Branch ${index + 1}`;

    if (!entry || typeof entry !== "object") {
      errors.push(`${label}: Must be an object`);
      return;
    }

    const obj = entry as Record<string, unknown>;

    for (const key of Object.keys(obj)) {
      if (!BRANCH_KEYS.includes(key as (typeof BRANCH_KEYS)[number])) {
        errors.push(
          `${label}: Unknown key "${key}". Allowed keys: ${BRANCH_KEYS.join(", ")}`
        );
      }
    }

    for (const key of BRANCH_KEYS) {
      if (!(key in obj)) {
        errors.push(`${label}: Missing required key "${key}"`);
      }
    }

    if (errors.some((message) => message.startsWith(label))) {
      return;
    }

    if (typeof obj.id !== "string") {
      errors.push(`${label}: "id" must be a string`);
      return;
    }
    if (typeof obj.name !== "string") {
      errors.push(`${label}: "name" must be a string`);
      return;
    }

    let id = obj.id.trim();
    if (!id) {
      id = newId();
    }
    if (seenIds.has(id)) {
      errors.push(`${label}: Duplicate id "${id}"`);
      return;
    }
    seenIds.add(id);

    if (!validateConditions(obj.conditions, label, errors)) {
      return;
    }

    let name = obj.name.trim();
    if (!name) {
      name = nextDefaultBranchName(usedNames);
    }
    usedNames.push(name);

    branches.push({
      id,
      name,
      conditions: obj.conditions as unknown[],
    });
  });

  if (errors.length > 0) {
    return { branches: [], errors };
  }

  return { branches, errors: [] };
}
